import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';

interface Partner {
  name: string;
  description: string;
  icon: string;
  features: string[];
  status: 'active' | 'coming-soon';
  color: string;
}

interface Integration {
  title: string;
  description: string;
  icon: string;
  link: string;
  category: 'api' | 'wallet' | 'defi' | 'infrastructure';
}

export const EcosystemPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('partners');

  const partners: Partner[] = [
    {
      name: 'Vault Partners',
      description: 'Certified vaults for secure gold storage and verification',
      icon: 'fas fa-vault',
      features: ['Secure Storage', 'Regular Audits', 'Insurance Coverage', 'Multi-location'],
      status: 'active',
      color: 'from-blue-500/20 to-blue-500/5'
    },
    {
      name: 'Exchange Partners',
      description: 'Leading cryptocurrency exchanges for liquidity and trading',
      icon: 'fas fa-exchange-alt',
      features: ['High Liquidity', 'Global Access', '24/7 Trading', 'Professional Tools'],
      status: 'active',
      color: 'from-green-500/20 to-green-500/5'
    },
    {
      name: 'Payment Partners',
      description: 'Payment processors for seamless fiat-to-crypto conversions',
      icon: 'fas fa-credit-card',
      features: ['Fiat On-ramp', 'Multiple Currencies', 'Fast Processing', 'Low Fees'],
      status: 'active',
      color: 'from-purple-500/20 to-purple-500/5'
    },
    {
      name: 'DeFi Partners',
      description: 'DeFi protocols for lending, borrowing, and yield generation',
      icon: 'fas fa-chart-line',
      features: ['Lending Platforms', 'Yield Farming', 'Staking Rewards', 'Cross-chain Compatibility'],
      status: 'coming-soon',
      color: 'from-orange-500/20 to-orange-500/5'
    }
  ];

  const integrations: Integration[] = [
    {
      title: 'RESTful APIs',
      description: 'Comprehensive APIs for seamless integration into existing platforms',
      icon: 'fas fa-code',
      link: '#',
      category: 'api'
    },
    {
      title: 'Sui Wallet Support',
      description: 'Compatible with major Sui Network wallets for easy access',
      icon: 'fas fa-wallet',
      link: '#',
      category: 'wallet'
    },
    {
      title: 'DeFi Protocol Integration',
      description: 'Connect with leading DeFi protocols for enhanced utility',
      icon: 'fas fa-link',
      link: '#',
      category: 'defi'
    },
    {
      title: 'Smart Contract Templates',
      description: 'Pre-built templates for common use cases and integrations',
      icon: 'fas fa-file-code',
      link: '#',
      category: 'infrastructure'
    },
    {
      title: 'SDK & Libraries',
      description: 'Software development kits for popular programming languages',
      icon: 'fas fa-box',
      link: '#',
      category: 'api'
    },
    {
      title: 'Oracles & Data Feeds',
      description: 'Real-time price feeds and market data integration',
      icon: 'fas fa-database',
      link: '#',
      category: 'infrastructure'
    }
  ];

  const technicalSpecs = [
    { label: 'Blockchain', value: 'Sui Network', icon: 'fas fa-link' },
    { label: 'Smart Contracts', value: 'Move Language', icon: 'fas fa-code' },
    { label: 'Consensus', value: 'Narwhal & Bullshark', icon: 'fas fa-project-diagram' },
    { label: 'Transaction Speed', value: '~120,000 TPS', icon: 'fas fa-tachometer-alt' },
    { label: 'Security Level', value: 'Enterprise-Grade', icon: 'fas fa-shield-alt' },
    { label: 'Finality', value: 'Instant', icon: 'fas fa-check-circle' }
  ];

  const filteredIntegrations = activeTab === 'all'
    ? integrations
    : integrations.filter(integration => integration.category === activeTab);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('ecosystem.title')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('ecosystem.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('ecosystem.description')}
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="section relative">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {['partners', 'integrations', 'infrastructure'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`group relative px-8 py-3 rounded-full transition-all duration-300 ${
                  activeTab === tab
                    ? 'bg-gold text-black font-bold'
                    : 'bg-gold/10 text-gold hover:bg-gold/20'
                }`}
              >
                <span className="relative z-10 flex items-center gap-2 capitalize">
                  <i className={`fas fa-${tab === 'partners' ? 'handshake' : tab === 'integrations' ? 'puzzle-piece' : 'microchip'} text-sm`}></i>
                  {tab}
                </span>
                {activeTab === tab && (
                  <div className="absolute inset-0 bg-gradient-to-r from-gold to-gold-light rounded-full"></div>
                )}
              </button>
            ))}
          </div>

          {/* Partners Section */}
          {activeTab === 'partners' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {partners.map((partner, index) => (
                <div key={index} className="partner-card group">
                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-20 h-20 bg-gradient-to-br ${partner.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <i className={`${partner.icon} text-3xl text-white`}></i>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${partner.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></div>
                        <span className={`text-sm font-semibold ${partner.status === 'active' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {partner.status === 'active' ? 'Active' : 'Coming Soon'}
                        </span>
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                      {partner.name}
                    </h3>

                    <p className="text-gray-400 leading-relaxed mb-6">
                      {partner.description}
                    </p>

                    <div className="space-y-3">
                      {partner.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-gray-400">
                          <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                            <i className="fas fa-check text-gold text-xs"></i>
                          </div>
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Integrations Section */}
          {(activeTab === 'integrations' || activeTab === 'all') && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {filteredIntegrations.map((integration, index) => (
                <div key={index} className="modern-card group">
                  <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${integration.icon} text-2xl text-gold`}></i>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                    {integration.title}
                  </h3>

                  <p className="text-gray-400 leading-relaxed mb-6">
                    {integration.description}
                  </p>

                  <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                    <span className="text-gold text-xs font-semibold uppercase tracking-wider">
                      {integration.category}
                    </span>
                  </div>

                  <Link
                    to={integration.link}
                    className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2"
                    aria-label={`Learn more about ${integration.title}`}
                  >
                    Learn More
                    <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                  </Link>

                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              ))}
            </div>
          )}

          {/* Infrastructure Section */}
          {activeTab === 'infrastructure' && (
            <div className="max-w-5xl mx-auto">
              <div className="glass-card">
                <div className="text-center mb-12">
                  <div className="w-24 h-24 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <i className="fas fa-microchip text-5xl text-gold"></i>
                  </div>
                  <h3 className="text-3xl font-black text-white mb-4">Technical Infrastructure</h3>
                  <p className="text-xl text-gray-400 leading-relaxed">
                    Built on the Sui Network for high performance and scalability
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {technicalSpecs.map((spec, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-gold/20 rounded-xl hover:bg-gold/5 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-gold/20 to-gold/5 rounded-xl flex items-center justify-center">
                          <i className={`${spec.icon} text-gold`}></i>
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">{spec.label}</div>
                          <div className="text-white font-semibold">{spec.value}</div>
                        </div>
                      </div>
                      <i className="fas fa-check-circle text-green-400"></i>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Ecosystem Growth</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Growing Together</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Our ecosystem continues to expand with new partnerships and integrations
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">50+</div>
              <div className="text-gray-400">Ecosystem Partners</div>
            </div>

            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">100+</div>
              <div className="text-gray-400">API Integrations</div>
            </div>

            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">$1B+</div>
              <div className="text-gray-400">TVL Locked</div>
            </div>

            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">24/7</div>
              <div className="text-gray-400">Uptime Guarantee</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section pb-20">
        <div className="container">
          <div className="cta-section relative">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 relative z-10">Join Our Ecosystem</h2>
            <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10">
              Partner with us to build the future of gold-backed digital assets
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center relative z-10">
              <Button variant="primary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                <span className="relative z-10 flex items-center">
                  <i className="fas fa-handshake mr-3 group-hover:rotate-12 transition-transform"></i>
                  Become a Partner
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Button>
              <Link to="/resources">
                <Button variant="secondary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">View Documentation</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};